import { useCallback, useState } from 'react';
import { Button, Group, rem } from '@mantine/core';
import { IconDownload, IconBuildingStore, IconCircleCheck, IconCircleX } from '@tabler/icons-react';
import { downloadBulkPdf } from '@api/documents';
import { useAuth } from '@providers/auth';
import Notification from '@components/notification/notification';
import { useDisclosure } from '@mantine/hooks';
import AssignCostCenterModal from '@components/AssignCostCenterModal/AssignCostCenterModal';
import BulkPayDocumentModal from '@components/BulkPayDocumentModal/BulkPayDocumentModal';
import { CommonHash } from '@types';

export interface BulkSelection {
  selectedUuids: Set<string>;
  toggleSelection: (uuid: string) => void;
  toggleSelectAll: () => void;
  clearSelection: () => void;
  setAllUuids: (uuids: string[]) => void;
  allSelected: boolean;
  someSelected: boolean;
}

export function useBulkSelection(): BulkSelection {
  const [selectedUuids, setSelectedUuids] = useState<Set<string>>(new Set());
  const [allUuids, setAllUuidsState] = useState<string[]>([]);

  const toggleSelection = useCallback((uuid: string) => {
    setSelectedUuids((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });
  }, []);

  const allSelected = allUuids.length > 0 && allUuids.every((uuid) => selectedUuids.has(uuid));
  const someSelected = allUuids.some((uuid) => selectedUuids.has(uuid));

  const toggleSelectAll = useCallback(() => {
    setSelectedUuids((prev) => (prev.size > 0 ? new Set() : new Set(allUuids)));
  }, [allUuids]);

  const clearSelection = useCallback(() => setSelectedUuids(new Set()), []);
  const setAllUuids = useCallback((uuids: string[]) => setAllUuidsState(uuids), []);

  return {
    selectedUuids,
    toggleSelection,
    toggleSelectAll,
    clearSelection,
    setAllUuids,
    allSelected,
    someSelected,
  };
}

export function BulkDownloadActions({
  bulkSelection,
  documents = [],
}: {
  bulkSelection: BulkSelection;
  documents?: CommonHash[];
}) {
  const { session } = useAuth();
  const [isDownloading, setIsDownloading] = useState(false);
  const [openedCostCenter, { open: openCostCenter, close: closeCostCenter }] = useDisclosure(false);
  const [openedMarkPaid, { open: openMarkPaid, close: closeMarkPaid }] = useDisclosure(false);
  const [openedMarkUnpaid, { open: openMarkUnpaid, close: closeMarkUnpaid }] = useDisclosure(false);
  const { selectedUuids } = bulkSelection;

  if (selectedUuids.size === 0) return null;

  const uuids = Array.from(selectedUuids);
  const selectedDocuments = documents.filter((doc) => selectedUuids.has(doc.uuid as string));

  const handleBulkDownload = async () => {
    if (!session?.token || selectedUuids.size === 0) return;
    setIsDownloading(true);
    try {
      await downloadBulkPdf(session.token, uuids);
    } catch {
      Notification({ type: 'error', message: 'Error al descargar los PDFs, intente más tarde' });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <Group gap="xs">
        <Button
          size="sm"
          variant="outline"
          loading={isDownloading}
          leftSection={<IconDownload style={{ width: rem(16), height: rem(16) }} stroke={1.5} />}
          onClick={handleBulkDownload}
        >
          Descargar PDFs ({selectedUuids.size})
        </Button>
        <Button
          size="sm"
          variant="outline"
          color="violet"
          leftSection={<IconBuildingStore style={{ width: rem(16), height: rem(16) }} stroke={1.5} />}
          onClick={openCostCenter}
        >
          Plan de cuenta ({selectedUuids.size})
        </Button>
        <Button
          size="sm"
          variant="outline"
          color="green"
          leftSection={<IconCircleCheck style={{ width: rem(16), height: rem(16) }} stroke={1.5} />}
          onClick={openMarkPaid}
        >
          Pagados ({selectedUuids.size})
        </Button>
        <Button
          size="sm"
          variant="outline"
          color="red"
          leftSection={<IconCircleX style={{ width: rem(16), height: rem(16) }} stroke={1.5} />}
          onClick={openMarkUnpaid}
        >
          No pagados ({selectedUuids.size})
        </Button>
      </Group>
      <AssignCostCenterModal
        opened={openedCostCenter}
        close={closeCostCenter}
        uuids={uuids}
      />
      <BulkPayDocumentModal
        opened={openedMarkPaid}
        close={closeMarkPaid}
        action="paid"
        selectedDocuments={selectedDocuments}
      />
      <BulkPayDocumentModal
        opened={openedMarkUnpaid}
        close={closeMarkUnpaid}
        action="unpaid"
        selectedDocuments={selectedDocuments}
      />
    </>
  );
}
